/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { updatePlanModal } from 'store/user';
import {
  Row,
  Col,
  Tabs,
  Space,
  Typography,
  Tag,
  Tooltip,
  Popover,
  Skeleton,
  Carousel,
  Button,
  Spin
} from 'antd';
import Alert from 'components/Alert';
import UserInfoCard from 'components/UserInfoCard';
import merchantAPI from 'services/merchants';
import KycCard from 'components/KycCard';
import ProductCard from 'components/ProductCard';
import EbzCarousel from 'components/EbzCarousel';
import CombineCard from 'components/CombineCard';
import { MerchantDetailTabMenu, slabType } from './const';
import MerchantDetailsSkeleton from './MerchantDetailsSkeleton';
import ProductTab from './ProductTab';

const MenuTabBarWrapper = styled.div`
  && {
    .ant-tabs-tab {
      padding: 12px 10px;
      font-size: 17px;
    }

    .ant-tabs-nav::before {
      border-bottom: 2px solid rgba(5, 5, 5, 0.06);
    }
  }
`;

const renderTabBarMenu = (props, DefaultTabBar) => {
  return (
    <MenuTabBarWrapper>
      <DefaultTabBar {...props} />
    </MenuTabBarWrapper>
  );
};

function MerchantDetailsPage() {
  const [hasUserInfo, setHasUserInfo] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});

  const [tabMenuItem, setTabMenuItem] = useState(MerchantDetailTabMenu);
  const [currentTabActiveKey, setCurrentTabActiveKey] = useState(MerchantDetailTabMenu[0].key);
  // redux state
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const configs = useSelector((state) => state.user.currentUser.configuration);
  const isKycUpdateAllowed = useSelector((state) => state.user.planPermissions.can_update_kyc);
  const isProductRequestAllowed = useSelector(
    (state) => state.user.planPermissions.can_request_product
  );
  const isMarkupAllowed = useSelector((state) => state.user.planPermissions.can_view_markup);
  const params = useParams();

  // functions for api calls
  const generateInitialValues = (products, slabKeys) => {
    const values = {};

    products.forEach((productItem) => {
      const productKey = productItem?.product_name;
      values[productKey] = {};

      productItem?.configuration.forEach((mode) => {
        const modeKey = mode?.mode_type;
        values[productKey][modeKey] = {
          is_enable: mode?.is_enable
        };

        mode?.data.forEach((typeObj) => {
          const typeKey = typeObj?.key;
          values[productKey][modeKey][typeKey] = {
            label: typeObj?.label,
            [slabKeys[0]]: {}
          };

          // Primary Slab
          typeObj?.range?.forEach((slabItem, idx) => {
            values[productKey][modeKey][typeKey][slabKeys[0]][idx] = {
              min_amount: slabItem?.min_amount,
              max_amount: slabItem?.max_amount,
              commercial_type: slabItem?.commercial_type,
              commission_type: slabItem?.commission_type,
              surcharge_flag: slabItem?.surcharge_flag,
              merchant_commercial: slabItem?.merchant_commercial,
              commercial_value: slabItem?.commercial_value,
              partner_commission: slabItem?.partner_commission,
              base_rate_type: slabItem?.base_rate_type,
              base_rate_value: slabItem?.base_rate_value,
              flat_fee: slabItem?.flat_fee
            };
          });

          // Differential Slab
          if (typeObj?.differential) {
            values[productKey][modeKey][typeKey][slabKeys[1]] = {
              label: typeObj?.differential_label
            };

            const differentialSection = values[productKey][modeKey][typeKey][slabKeys[1]];

            Object.entries(typeObj.differential).forEach(([brandKey, brands]) => {
              differentialSection[brandKey] = {};
              brands.forEach((slabBrand, entryIndex) => {
                differentialSection[brandKey][entryIndex] = {
                  min_amount: slabBrand?.min_amount,
                  max_amount: slabBrand?.max_amount,
                  commercial_type: slabBrand?.commercial_type,
                  surcharge_flag: slabBrand?.surcharge_flag,
                  commission_type: slabBrand?.commission_type,
                  merchant_commercial: slabBrand?.merchant_commercial,
                  partner_commission: slabBrand?.partner_commission,
                  flat_fee: slabBrand?.flat_fee
                };
              });
            });
          }
        });
      });
    });
    return values;
  };

  const buildTabMenuItems = (menuItems, configData, initialFormValues) => {
    return menuItems.map((menuItem) => {
      if (menuItem.key === MerchantDetailTabMenu[0].key) {
        return {
          ...menuItem,
          children: <ProductTab products={configData} initial={initialFormValues} />
        };
      }
      return menuItem;
    });
  };

  const fetchMerchantInfo = async (payload) => {
    const response = await merchantAPI.info(payload);
    if (!response.success) {
      setHasUserInfo(false);
      Alert('something went wrong', 'Please contact support team for this', 'error');
    } else {
      setHasUserInfo(true);
      setUserInfo(response?.data ?? []);
    }
  };
  const handleTabChange = (e) => {
    setCurrentTabActiveKey(e);
  };

  const fetchInfo = () => {
    fetchMerchantInfo({ merchant_id: parseInt(params.id, 10) });
  };

  const fetchMerchantMarkups = async (payload) => {
    setLoading(true);
    const response = await merchantAPI.markupConfigData(payload);

    if (!response.success) {
      console.log('something went wrong');
    }
    setData(response?.data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    if (isMarkupAllowed) {
      fetchInfo();
      fetchMerchantMarkups({ merchant_id: parseInt(params.id, 10) });
    }
  }, [isMarkupAllowed]);

  useEffect(() => {
    if (Object.keys(data).length === 0) return;

    const initialValues = generateInitialValues(data, slabType);
    const tabMenuData = buildTabMenuItems(tabMenuItem, data, initialValues);

    setTabMenuItem(tabMenuData);
  }, [data]);

  return (
    <div style={{ padding: '20px' }}>
      <Row gutter={[20, 20]}>
        <Col span={9}>
          {hasUserInfo ? (
            <UserInfoCard
              name={userInfo?.personal_details?.name ?? '-'}
              email={userInfo?.personal_details?.email ?? '-'}
              phone={userInfo?.personal_details?.phone_number ?? '-'}
              businessName={userInfo?.personal_details?.business_name ?? '-'}
              mid={userInfo?.personal_details?.merchant_id ?? '-'}
              address={userInfo?.personal_details?.address ?? '-'}
            />
          ) : (
            <MerchantDetailsSkeleton />
          )}
        </Col>
        <Col span={7}>
          {hasUserInfo === true ? (
            <CombineCard
              gtv={userInfo?.gtv_profit_card_details ?? {}}
              login={userInfo?.login_card_details ?? {}}
              isKycComplte={userInfo?.kyc_product_card_details?.is_kyc_complete ?? '-'}
            />
          ) : (
            <Row gutter={[20, 20]} style={{ maxWidth: '100%' }}>
              <Col
                span={24}
                style={{
                  background:
                    'transparent linear-gradient(119deg, #FFFFFF 35%, #E5FDFD 100%) 0% 0% no-repeat padding-box',
                  boxShadow: '0px 1px 6px #00000014',
                  borderRadius: '10px'
                }}
              >
                <Skeleton
                  active
                  paragraph={{
                    rows: 2
                  }}
                />
              </Col>
              <Col
                span={24}
                style={{
                  background:
                    'transparent linear-gradient(119deg, #FFFFFF 35%, #E5FDFD 100%) 0% 0% no-repeat padding-box',
                  boxShadow: '0px 1px 6px #00000014',
                  borderRadius: '10px'
                }}
              >
                <Skeleton
                  active
                  paragraph={{
                    rows: 2
                  }}
                />
              </Col>
            </Row>
          )}
        </Col>
        <Col
          span={8}
          style={{
            borderRadius: '13px'
          }}
        >
          {hasUserInfo ? (
            <EbzCarousel effect="fade">
              <KycCard
                bankStatus={userInfo?.kyc_product_card_details?.bank_status ?? '-'}
                businessStatus={userInfo?.kyc_product_card_details?.business_status ?? '-'}
                basicStatus={userInfo?.kyc_product_card_details?.basic_status ?? '-'}
                merchantId={userInfo?.personal_details?.merchant_id ?? null}
                isKycComplete={userInfo?.kyc_product_card_details?.is_kyc_complete ?? null}
                updateKyc={configs.permission_configs.can_update_kyc ?? undefined}
              />
              <ProductCard
                products={userInfo?.kyc_product_card_details?.products ?? {}}
                merchantId={userInfo?.personal_details?.merchant_id ?? null}
                fetchMerchantInfo={fetchInfo}
                requestProduct={isProductRequestAllowed ?? undefined}
              />
            </EbzCarousel>
          ) : (
            <Row
              gutter={[16, 16]}
              style={{
                boxShadow: 'rgb(0 0 0 / 6%) 0px 3px 6px 4px',
                borderRadius: '10px',
                backgroundColor: 'white'
              }}
            >
              <Col span={24}>
                <Skeleton
                  active
                  paragraph={{
                    rows: 4
                  }}
                />
              </Col>
            </Row>
          )}
        </Col>
      </Row>
      {tabMenuItem.length > 0 && (
        <Spin spinning={loading}>
          <div style={{ marginTop: '25px' }}>
            <Tabs
              activeKey={currentTabActiveKey}
              size="large"
              items={tabMenuItem}
              renderTabBar={renderTabBarMenu}
              onChange={handleTabChange}
            />
          </div>
        </Spin>
      )}
    </div>
  );
}

export default MerchantDetailsPage;
